import { Link } from 'react-router-dom';
import { ArrowUpRight, Rocket, Music, BookOpen, Layers, Terminal, Activity, Disc, Sparkles, ChevronDown } from 'lucide-react';
import SEO from '../components/SEO';
import NeuralBrain from '../components/NeuralBrain';
import { personSchema, websiteSchema } from '../data/schemas';

const HomePage = () => {
  return (
    <div className="pb-10 sm:pb-20 relative font-sans text-gray-200">
      <SEO
        title="NotKoneko — CS, Neuroscience & Cognitive Systems"
        description="Software engineer and researcher architecting predictive data models, specialized API engines, and native mobile applications at the intersection of human cognition and machine intelligence."
        path="/"
        keywords={['Koneko', 'NotKoneko', 'NottKoneko', 'Portfolio', 'NeuroSyn', 'Lyrika-Stanza', 'ASU', 'Computer Science', 'Neuroscience', 'Cognitive Systems', 'Neural Atlas']}
        schema={{
          '@context': 'https://schema.org',
          '@graph': [personSchema, websiteSchema]
        }}
      />

      {/* ========================================================================= */}
      {/* FULL VIEWPORT WELCOME STAGE (EDGE-TO-EDGE 3D NEURAL ATLAS) */}
      {/* ========================================================================= */}
      <section className="relative w-full min-h-[100dvh] sm:min-h-[calc(100vh-4.5rem)] flex flex-col justify-end px-4 sm:px-12 lg:px-20 pb-16 sm:pb-20 overflow-hidden">
        {/* 3D Interactive Three.js Neural Brain Background (Full Viewport) */}
        <div className="absolute inset-0 z-0">
          <NeuralBrain className="w-full h-full" />
        </div>

        {/* Ambient Cosmic Lights */}
        <div className="absolute top-1/4 right-1/4 w-[32rem] h-[32rem] bg-purple-600/10 rounded-full blur-[140px] pointer-events-none -z-10" />
        <div className="absolute bottom-10 left-1/4 w-[32rem] h-[32rem] bg-pink-600/10 rounded-full blur-[140px] pointer-events-none -z-10" />

        {/* WELCOME HERO OVERLAY (Reference Style) */}
        <div className="relative z-30 flex flex-col items-start max-w-2xl space-y-3 pointer-events-none">
          <div className="text-xs font-mono tracking-widest text-purple-400/90 uppercase flex items-center gap-2 pointer-events-auto">
            ABOUT // NOTKONEKO <span className="inline-block w-8 h-[1px] bg-purple-500/40" />
          </div>

          <h1 className="text-4xl sm:text-6xl md:text-7xl font-serif text-white tracking-wide leading-tight drop-shadow-[0_4px_30px_rgba(0,0,0,0.95)] pointer-events-auto">
            NotKoneko <span className="font-mono text-2xl sm:text-4xl font-light text-fuchsia-400">=^..^=</span>
          </h1>

          <div className="text-xs sm:text-sm font-mono text-purple-300/90 tracking-wider pointer-events-auto">
            SOFTWARE ENGINEER // COGNITIVE NEUROSCIENCE // ASU
          </div>

          <p className="text-sm sm:text-base text-gray-300 font-serif italic leading-relaxed pointer-events-auto">
            — Applying deep learning, biometric iOS predictive engines, and computational neural models to lay the foundation for neural regeneration.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-3 pt-3 pointer-events-auto">
            <Link
              to="/projects"
              className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-mono text-xs font-bold rounded-xl transition-all duration-200 flex items-center gap-2 shadow-lg shadow-purple-500/25 hover:scale-[1.02] active:scale-95 border border-purple-400/30"
            >
              <Rocket className="w-3.5 h-3.5" /> [ 01 // EXPLORE SYSTEMS ]
            </Link>
            <Link
              to="/projects/discordbots/lyrika-stanza"
              className="px-5 py-2.5 bg-[#0e0a1a]/90 hover:bg-purple-950/60 border border-fuchsia-500/40 hover:border-fuchsia-400 text-fuchsia-300 font-mono text-xs font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 shadow-lg shadow-fuchsia-500/10 backdrop-blur-md hover:scale-[1.02] active:scale-95"
            >
              <Music className="w-3.5 h-3.5" /> [ 02 // LYRIKA-STANZA LIVE ]
            </Link>
            <Link
              to="/ideas"
              className="px-4 py-2.5 bg-white/[0.03] hover:bg-white/[0.08] border border-white/[0.1] text-gray-300 hover:text-white font-mono text-xs rounded-xl transition-all duration-200 flex items-center gap-1.5 backdrop-blur-md"
            >
              <BookOpen className="w-3.5 h-3.5 text-gray-400" /> [ 03 // RESEARCH ]
            </Link>
          </div>
        </div>

        {/* Animated Bobbing Scroll Indicator (Upside-down ^) */}
        <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-30 pointer-events-auto">
          <button
            onClick={() => {
              const target = document.getElementById('explore-archive');
              if (target) {
                target.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="group flex flex-col items-center justify-center p-2 text-purple-400 hover:text-fuchsia-300 transition-colors focus:outline-none cursor-pointer"
            aria-label="Scroll down to explore archive"
            title="Scroll to explore"
          >
            <span className="text-[10px] font-mono tracking-widest uppercase text-purple-400/70 group-hover:text-purple-300 transition-colors mb-0.5">
              SCROLL
            </span>
            <ChevronDown className="w-5 h-5 text-fuchsia-400 group-hover:text-fuchsia-300 animate-bounce duration-1000" />
          </button>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* LOWER CONTENT CONTAINER */}
      {/* ========================================================================= */}
      <div id="explore-archive" className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 space-y-16 sm:space-y-24">
        {/* TELEMETRY METRIC STRIP (Clean 3-Column Grid) */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 font-mono">
          <div className="p-5 rounded-2xl bg-[#090714] border border-purple-500/20 backdrop-blur-md transition-all hover:border-fuchsia-500/50 hover:shadow-[0_0_25px_rgba(232,121,249,0.15)] group">
            <div className="flex items-center justify-between text-[11px] text-fuchsia-400 uppercase tracking-widest mb-1.5">
              <span>[ 01 // ENGINE ]</span>
              <Activity className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
            </div>
            <span className="block text-2xl sm:text-3xl font-extrabold text-fuchsia-300 tracking-tight">Swift 6</span>
            <span className="text-[11px] text-gray-400 tracking-wider block mt-0.5">NeuroSyn Biometrics</span>
          </div>

          <div className="p-5 rounded-2xl bg-[#090714] border border-purple-500/20 backdrop-blur-md transition-all hover:border-pink-500/50 hover:shadow-[0_0_25px_rgba(244,114,182,0.15)] group">
            <div className="flex items-center justify-between text-[11px] text-pink-400 uppercase tracking-widest mb-1.5">
              <span>[ 02 // FLEET ]</span>
              <Disc className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
            </div>
            <span className="block text-2xl sm:text-3xl font-extrabold text-pink-300 tracking-tight">24/7</span>
            <span className="text-[11px] text-gray-400 tracking-wider block mt-0.5">Wispbyte Daemon</span>
          </div>

          <div className="p-5 rounded-2xl bg-[#090714] border border-purple-500/20 backdrop-blur-md transition-all hover:border-purple-400/50 hover:shadow-[0_0_25px_rgba(232,121,249,0.15)] group">
            <div className="flex items-center justify-between text-[11px] text-fuchsia-400 uppercase tracking-widest mb-1.5">
              <span>[ 03 // PROTOCOL ]</span>
              <Sparkles className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
            </div>
            <span className="block text-2xl sm:text-3xl font-extrabold text-purple-200 tracking-tight">100%</span>
            <span className="text-[11px] text-gray-400 tracking-wider block mt-0.5">Custom Architecture</span>
          </div>
        </section>

        {/* FEATURED SYSTEMS // CYBERPUNK DOSSIER CARDS */}
        <section className="space-y-6 sm:space-y-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-3 border-b border-purple-500/20 pb-4">
            <div>
              <span className="text-xs font-mono text-fuchsia-400 uppercase tracking-widest block mb-1">
                SYSTEMS ARCHIVE // DOSSIERS
              </span>
              <h2 className="text-2xl sm:text-3xl font-serif text-white tracking-wide">
                Featured Subsystems
              </h2>
            </div>
            <Link
              to="/projects"
              className="text-xs font-mono text-purple-300 hover:text-fuchsia-300 transition-colors flex items-center gap-1.5 group"
            >
              VIEW FULL DIRECTORY <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            {/* DOSSIER 01: NeuroSyn */}
            <Link
              to="/projects/neurosyn"
              id="dossier-neurosyn"
              className="group relative bg-[#090714] hover:bg-[#0e0a1e] p-6 sm:p-7 rounded-2xl border border-purple-500/20 hover:border-purple-400/80 transition-all duration-300 flex flex-col justify-between overflow-hidden shadow-2xl"
            >
              <div className="absolute top-0 right-0 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl group-hover:bg-purple-500/20 transition-all pointer-events-none" />

              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-mono text-purple-400 tracking-widest uppercase">
                    [ 01 // NEUROSYN.SYS ]
                  </span>
                  <span className="text-[11px] font-mono px-2.5 py-1 bg-purple-950/60 text-purple-200 border border-purple-700/50 rounded-full">
                    iOS Mobile App
                  </span>
                </div>

                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0">
                    <img src="/neurosyn-icon.png" alt="NeuroSyn Icon" className="w-full h-full object-contain rounded-xl" />
                  </div>
                  <h3 className="text-2xl font-serif text-white group-hover:text-purple-200 transition-colors flex items-center justify-between w-full">
                    NeuroSyn
                    <ArrowUpRight className="w-4 h-4 text-purple-400 group-hover:text-fuchsia-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </h3>
                </div>

                <p className="text-gray-300 text-xs sm:text-sm leading-relaxed mb-6 font-normal">
                  Proprietary iOS biometric application correlating digital screen telemetry and TikTok watch histories with Apple HealthKit data to predict cognitive fatigue and optimize sleep recovery.
                </p>
              </div>

              <div className="pt-4 border-t border-purple-500/20 flex items-center justify-between text-xs font-mono text-purple-300/90">
                <span>SWIFT 6 • HEALTHKIT • SWIFTUI</span>
                <span className="text-fuchsia-400 font-semibold">◆ PRIVATE BETA</span>
              </div>
            </Link>

            {/* DOSSIER 02: Lyrika-Stanza */}
            <Link
              to="/projects/discordbots/lyrika-stanza"
              id="dossier-lyrika"
              className="group relative bg-[#090714] hover:bg-[#0e0a1e] p-6 sm:p-7 rounded-2xl border border-purple-500/20 hover:border-fuchsia-400/80 transition-all duration-300 flex flex-col justify-between overflow-hidden shadow-2xl"
            >
              <div className="absolute top-0 right-0 w-48 h-48 bg-fuchsia-500/10 rounded-full blur-3xl group-hover:bg-fuchsia-500/20 transition-all pointer-events-none" />

              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-mono text-fuchsia-400 tracking-widest uppercase">
                    [ 02 // LYRIKA-STANZA.SYS ]
                  </span>
                  <span className="text-[11px] font-mono px-2.5 py-1 bg-fuchsia-950/60 text-fuchsia-200 border border-fuchsia-700/50 rounded-full flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Live Daemon
                  </span>
                </div>

                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-pink-500 via-purple-600 to-indigo-600 p-0.5 overflow-hidden">
                    <img src="/lyrika-avatar.webp" alt="Lyrika Avatar" className="w-full h-full object-cover rounded-[10px]" />
                  </div>
                  <h3 className="text-2xl font-serif text-white group-hover:text-fuchsia-200 transition-colors flex items-center justify-between w-full">
                    Lyrika-Stanza
                    <ArrowUpRight className="w-4 h-4 text-fuchsia-400 group-hover:text-pink-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </h3>
                </div>

                <p className="text-gray-300 text-xs sm:text-sm leading-relaxed mb-6 font-normal">
                  Time-synchronized live lyric streaming engine for Discord voice channels with microsecond offset calibration (⏪ -0.5s / ⏩ +0.5s) deployed 24/7 on Wispbyte cloud.
                </p>
              </div>

              <div className="pt-4 border-t border-purple-500/20 flex items-center justify-between text-xs font-mono text-fuchsia-300/90">
                <span>NODE.JS • LRCLIB • WISPBYTE</span>
                <span className="text-pink-400 font-semibold">◆ VERIFIED BOT</span>
              </div>
            </Link>

            {/* DOSSIER 03: Tamed */}
            <Link
              to="/projects/tamed"
              id="dossier-tamed"
              className="group relative bg-[#090714] hover:bg-[#0e0a1e] p-6 sm:p-7 rounded-2xl border border-purple-500/20 hover:border-indigo-400/80 transition-all duration-300 flex flex-col justify-between overflow-hidden shadow-2xl"
            >
              <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl group-hover:bg-indigo-500/20 transition-all pointer-events-none" />

              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-mono text-indigo-400 tracking-widest uppercase">
                    [ 03 // TAMED.SYS ]
                  </span>
                  <span className="text-[11px] font-mono px-2.5 py-1 bg-indigo-950/60 text-indigo-200 border border-indigo-700/50 rounded-full">
                    Web Application
                  </span>
                </div>

                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0">
                    <img src="/tamed-icon.webp" alt="Tamed Icon" className="w-full h-full object-contain rounded-xl" />
                  </div>
                  <h3 className="text-2xl font-serif text-white group-hover:text-indigo-200 transition-colors flex items-center justify-between w-full">
                    Tamed
                    <ArrowUpRight className="w-4 h-4 text-indigo-400 group-hover:text-blue-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </h3>
                </div>

                <p className="text-gray-300 text-xs sm:text-sm leading-relaxed mb-6 font-normal">
                  Gamified habit accountability web application with real-time Supabase PostgreSQL synchronization, dual-role partner dashboards, and Zustand state persistence.
                </p>
              </div>

              <div className="pt-4 border-t border-purple-500/20 flex items-center justify-between text-xs font-mono text-indigo-300/90">
                <span>REACT 19 • SUPABASE • ZUSTAND</span>
                <span className="text-indigo-400 font-semibold">◆ PRODUCTION</span>
              </div>
            </Link>

            {/* DOSSIER 04: The Forgetting Paradox */}
            <Link
              to="/ideas/forgetting-curve-in-ai"
              id="dossier-research"
              className="group relative bg-[#090714] hover:bg-[#0e0a1e] p-6 sm:p-7 rounded-2xl border border-purple-500/20 hover:border-emerald-400/80 transition-all duration-300 flex flex-col justify-between overflow-hidden shadow-2xl"
            >
              <div className="absolute top-0 right-0 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl group-hover:bg-emerald-500/20 transition-all pointer-events-none" />

              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-mono text-emerald-400 tracking-widest uppercase">
                    [ 04 // SYNTHESIS.SYS ]
                  </span>
                  <span className="text-[11px] font-mono px-2.5 py-1 bg-emerald-950/60 text-emerald-200 border border-emerald-700/50 rounded-full">
                    Synthesis Paper
                  </span>
                </div>

                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center justify-center text-emerald-400 font-mono font-bold text-sm">
                    <BookOpen className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h3 className="text-2xl font-serif text-white group-hover:text-emerald-200 transition-colors flex items-center justify-between w-full">
                    The Forgetting Paradox
                    <ArrowUpRight className="w-4 h-4 text-emerald-400 group-hover:text-teal-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </h3>
                </div>

                <p className="text-gray-300 text-xs sm:text-sm leading-relaxed mb-6 font-normal">
                  Comparative analysis of biological synaptic decay and computational neural network dynamics to mitigate catastrophic interference in modern artificial intelligence models.
                </p>
              </div>

              <div className="pt-4 border-t border-purple-500/20 flex items-center justify-between text-xs font-mono text-emerald-300/90">
                <span>NEUROSCIENCE • ML • COGNITIVE SCI</span>
                <span className="text-emerald-400 font-semibold">◆ PUBLISHED</span>
              </div>
            </Link>
          </div>
        </section>

        {/* DEVELOPER TELEMETRY & DIAGNOSTICS HUD */}
        <section className="bg-[#070510]/95 border border-purple-500/25 rounded-2xl p-5 sm:p-7 font-mono text-xs shadow-2xl relative overflow-hidden backdrop-blur-xl">
          <div className="flex items-center justify-between mb-4 border-b border-purple-500/20 pb-3">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
              <span className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
              <span className="text-purple-400 ml-2 text-[11px] flex items-center gap-1">
                <Terminal className="w-3 h-3 inline text-fuchsia-400" /> telemetry@notkoneko.space
              </span>
            </div>
            <span className="text-[10px] text-fuchsia-400 uppercase tracking-widest flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              NEURAL ATLAS // 100% NOMINAL
            </span>
          </div>

          <div className="space-y-1.5 text-gray-300 text-[11px] sm:text-xs leading-relaxed overflow-x-auto">
            <p className="text-purple-400/80">$ atlas --diagnostics --all-subsystems</p>
            <p className="text-purple-300">[OK] ASU Universal Learner Node Synchronized</p>
            <p className="text-fuchsia-300">[OK] NeuroSyn Swift 6 Biometric HealthKit Daemon Listening</p>
            <p className="text-pink-300">[OK] Lyrika-Stanza Wispbyte Fleet Active (24/7 Uptime)</p>
            <p className="text-rose-300">[OK] Cognitive Syntheses &amp; Bioacoustic Telemetry Indexed</p>
            <p className="text-purple-400/60 pt-1 animate-pulse">&gt; Atlas waiting for user directive...</p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HomePage;